'use client'

import {useDevice, useResizeObserver, useScrollRatio, useSession, useTranslate} from '@local/hooks'
import {clamp, cubicBezier, precision} from '@local/utils/maths'
import {RefObject, useEffect, useRef, useState} from 'react'
import {ProductSliderCard, ProductSliderCardProps} from './ProductSliderCard'
import {Button, ComingSoonLogo, ComingSoonText, Container, Pastil, SlideshowNative} from '@local/ui/components'
import {cx} from '@local/utils/cx'
import {offsetLeft, offsetTop} from '@local/utils/offset'
import {ProductSliderListItem} from './ProductSliderListItem'

import {ProductSliderSlideout} from './ProductSliderSlideout'
import {LayoutSwitch} from './LayoutSwitch'
import {useScrollStore} from '@local/stores/stores'
import {AnimationControls, TimelineDefinition, timeline} from 'motion'
import {quadOut, quartInOut, quartOut, quintInOut} from 'eases'
import {devices, screens} from '@local/config/dist/theme'

export interface InactiveProps {
	isDisabled: boolean
	tag: string
	message: string
}

export interface TitlesProps {
	title:string;
	inactive?: InactiveProps,
	id: string
	itemsCount: number
	startIndex: number
}

export interface ProductSlideshowProps {
	titles: Array<TitlesProps>
	items: Array<ProductSliderCardProps>
	notifyPanel: {
		title: string
		text: string
		mailchimpId: string
		successMessage: string
	}
	intro:{
		surtitle: string
		text: string
	}
}

const DEFAULT_INTRO_PLAYED = 'notPlayed'

export const ProductSlideshow = ({items, titles, notifyPanel, intro}:ProductSlideshowProps) => {
	const scrollTo = useScrollStore(state => state.scrollTo)
	const [isIntroPlayed, setIntroPlayed] = useSession<string>('isIntroPlayed', DEFAULT_INTRO_PLAYED)
	const [layout, setLayout] = useState<'grid' | 'list'>('grid')
	const [isOpen, setIsOpen] = useState<boolean>(false)
	const [activeRetreat, setActiveRetreat] = useState<string>('')
	const onClickClose = () => {
		setIsOpen(false)
		setActiveRetreat('')
	}

	const containerRef = useRef(null)
	const switchRef = useRef<HTMLDivElement>(null)
	const slideshowRef = useRef(null)
	const childrenCardRefs = useRef<Array<HTMLDivElement>>([])
	const childrenListItemRefs = useRef<Array<HTMLDivElement>>([])
	const headingRefs = useRef<Array<HTMLDivElement>>([])
	const headingContainerRef = useRef<HTMLDivElement>(null)
	const headingOffsets = useRef<Array<number>>([])
	const slideshowWidth = useRef(0)
	const childWidth = useRef(0)

	const prevRatio = useRef(0)

	const [childrenOffsets, setChildrenOffset] = useState<Array<number>>([])
	const [activeHeading, setActiveHeader] = useState<number>(0)
	const activeHeadingRef = useRef<number>(0)
	const headingTimeout = useRef(null)
	const [slideshowDisabled, setSlideshowDisabled] = useState<boolean>(false)

	const locale = useTranslate()
	const resizeObserver: RefObject<ResizeObserver> = useRef(null)
	const handleResize = entries => {
		entries.forEach((entry) => {
			const target = entry.target
			target.__width = entry.target.offsetWidth
			target.__xInit = entry.target.offsetLeft
			if (!target.__x) target.__x = 0

			if (resizeObserver.current) {
				resizeObserver.current.unobserve(target)
			}
		})
	}

	useEffect(() => {
		resizeObserver.current = new IntersectionObserver(handleResize)
		return () => {
			if (resizeObserver.current) {
				resizeObserver.current.disconnect()
			}
		}
	}, [])

	useResizeObserver(containerRef, ({borderBoxSize}) => {
		slideshowWidth.current = slideshowRef.current.node.offsetWidth
		childWidth.current = slideshowWidth.current / items.length

		const _slideshowDisabled = borderBoxSize[0].inlineSize > slideshowWidth.current || borderBoxSize[0].inlineSize < screens[devices.tablet.screen]
		setSlideshowDisabled(_slideshowDisabled)
		if (_slideshowDisabled) return

		const vw = document.body.clientWidth / 100
		let remRatio = 0
		let topScrollPadding = 0
		if (layout === 'list') {
			const sbw = parseFloat(document.documentElement.style.getPropertyValue('--sbw'))
			if (borderBoxSize[0].inlineSize < screens[devices.tablet.screen]) {
				remRatio = Math.min(4.26667 * vw - sbw.current - 0.04267, 20.267)
				topScrollPadding = 4.75 * remRatio
			} else if (borderBoxSize[0].inlineSize >= screens[devices.tablet.screen] && borderBoxSize[0].inlineSize < screens[devices.desktop.screen]) {
				remRatio = 2.08333 * vw - sbw.current - 0.02083
				topScrollPadding = 4.75 * remRatio
			} else {
				remRatio = Math.min(1.11111 * vw - sbw.current - 0.01111, 18.667)
				topScrollPadding = 21 * remRatio
			}
		}

		// children offsets for navigation
		if (childrenCardRefs.current.length && resizeObserver.current) {
			const childrenOffsets: Array<number> = []
			childrenCardRefs.current.forEach((child, index) => {
				const prevId = items[index - 1]?.id
				const currentId = items[index]?.id
				if (currentId && (index === 0 || prevId !== currentId)) {
					const left = offsetLeft(child)
					const top = offsetTop(childrenListItemRefs.current[index])
					if (layout === 'grid') {
						childrenOffsets.push(left)
					} else {
						childrenOffsets.push(top - topScrollPadding)
					}
				}
				resizeObserver.current.observe(child)
			})
			setChildrenOffset(childrenOffsets)
		}

		if (headingRefs.current) {
			headingRefs.current.forEach((child) => {
				headingOffsets.current.push(offsetLeft(child))
			})
		}
	}, [layout])

	useScrollRatio(containerRef, (ratio, {viewport, scrollY}) => {
		if (slideshowDisabled || prevRatio.current === ratio) return

		const positionX = clamp(ratio, 0, 1) * (slideshowWidth.current - viewport.innerWidth) || 0

		// children translation
		childrenCardRefs.current.forEach((child) => {
			const elementX = child.__xInit - positionX
			if (!elementX) return

			const inView = elementX + child.__width >= 0 && elementX <= viewport.innerWidth
			const realtiveTranslate = -precision(positionX / child.__width * 100, 3)

			if (!inView) {
				child.style.transform = ``
				child.style.visibility = `hidden`
				child.style.willChange = ``
			} else {
				child.style.transform = `translate3d(${realtiveTranslate}%,0,0)`
				child.style.visibility = `visible`
				child.style.willChange = `transform`
			}
		})

		// heading transition
		const _lastIndex = childrenOffsets.map(offset => {
			if (layout === 'grid') {
				return ratio >= (offset / slideshowWidth.current)
			} else {
				return scrollY >= offset - 1
			}
		}).lastIndexOf(true)
		const lastIndex = _lastIndex === -1 ? 0 : _lastIndex
		headingRefs.current.forEach(() => {
			// use activeHeadingRef because ref is faster than state updates
			if (lastIndex !== activeHeadingRef.current && !headingTimeout.current) {
				activeHeadingRef.current = lastIndex
				setActiveHeader(lastIndex)
			}
		})

		prevRatio.current = ratio
	},
	{offset: [[0, 0], [1, 1]]},
	[childrenCardRefs.current, childrenOffsets, layout, slideshowDisabled])

	useEffect(() => {
		if (headingContainerRef.current) {
			headingContainerRef.current.scrollToIndex(activeHeading)
		}
	}, [activeHeading])

	// const handleHeadingClick = (index: number) => {
	// 	activeHeadingRef.current = index
	// 	setActiveHeader(index)
	// 	if (headingContainerRef.current) {
	// 		headingContainerRef.current.scrollToIndex(index)
	// 	}

	// 	// to prevent useScrollRatio from triggering a state update
	// 	headingTimeout.current = window.setTimeout(() => {
	// 		headingTimeout.current = null
	// 	}, layout === 'grid' ? 500 : 750)
	// }

	const handleLayout = (layout: 'grid' | 'list') => {
		setLayout(layout)
		scrollTo(0, {immediate: true})
		window.scrollTo({top: 0, behavior: 'instant'})
	}

	const handleNotifyClick = (retreat: string) => {
		setActiveRetreat(retreat)
		setIsOpen(true)
	}

	// Intro Animation
	const logoRef = useRef<HTMLSpanElement>(null)
	const textRef = useRef<HTMLDivElement>(null)
	const gridSlideshowRef = useRef<HTMLDivElement>(null)
	const tl = useRef<AnimationControls>(null)
	const device = useDevice()
	useEffect(() => {
		const startMotion = () => {
			if (logoRef.current && textRef.current) {
				const splitTextChars = [...textRef.current.querySelectorAll('.splitTextChar')]
				const charsMotion = splitTextChars.map((char, index) => ([
					char,
					{opacity: 1},
					{
						duration: 0.3,
						at: index === 0 ? '-1' : '<',
						delay: cubicBezier(index / splitTextChars.length, 0.83, 0, 0.17, 1) * splitTextChars.length * 0.05,
						allowWebkitAcceleration: true,
					},
				]))

				logoRef.current.style.transform = `translate3d(0, calc(var(--screenHeightMin)/2 - ${device === 'desktop' ? 2.5 : 2}rem*2), 0) scale(${device === 'desktop' ? 3.25 : 2.125})`

				const sequence = [
					[
						gridSlideshowRef.current,
						{opacity: 0, transform: 'translate3d(0,20%,0)', visibility: 'hidden'},
						{duration: 0.01, at: 0},
					],
					[
						gridSlideshowRef.current,
						{opacity: 0, transform: 'translate3d(0,100%,0)', visibility: 'hidden'},
						{duration: 0.01, at: 0},
					],
					// logo animation start
					[logoRef.current, {opacity: 1}, {duration: 1.5, easing: quadOut}],
					[logoRef.current, {transform: 'translate3d(0,0,0) scale(1)'}, {duration: 1.5, at: 0.75, easing: quintInOut}],
					// logo animation end

					// text animation start
					[
						textRef.current,
						{opacity: 1, transform: 'scaleX(1) scaleY(1) translateY(0) translateZ(0)', visibility: 'visible'},
						{duration: 1.5, at: 0.80, easing: quartInOut},
					],
					...charsMotion,
					[
						textRef.current,
						{opacity: 0, transform: 'scaleX(0.9) scaleY(0.9) translateY(-50%) translateZ(0)', visibility: 'hidden'},
						{duration: 1.4, easing: quintInOut},
					],
					// text animation end
					// grid slideshow animation start
					[
						gridSlideshowRef.current,
						{opacity: 1, transform: 'translate3d(0,0,0)', visibility: 'visible'},
						{duration: 0.8, at: '-0.6', easing: quartOut},
					],
					[
						switchRef.current,
						{opacity: 1, transform: 'translate3d(0,0,0)', visibility: 'visible'},
						{duration: 0.4, at: '-0.6', easing: quartOut},
					],
					// grid slideshow animation end
				] as TimelineDefinition

				tl.current = timeline(sequence)
				tl.current.finished.then((e) => {
					if (e) {
						setIntroPlayed('played')
					}
				})
			}
		}

		if (isIntroPlayed === DEFAULT_INTRO_PLAYED) {
			startMotion()
		} else {
			if (logoRef.current) {
				logoRef.current.style.opacity = '1'
				logoRef.current.style.visibility = 'visible'
			}
			if (gridSlideshowRef.current) {
				gridSlideshowRef.current.style.opacity = '1'
				gridSlideshowRef.current.style.visibility = 'visible'
			}
			if (switchRef.current) {
				switchRef.current.style.opacity = '1'
				switchRef.current.style.visibility = 'visible'
			}
		}
	}, [logoRef.current, textRef.current, gridSlideshowRef.current])

	return (
		<>
			<LayoutSwitch
				ref={switchRef}
				className='lg-max:hidden opacity-0 transfrom-gpu translate-y-full'
				activeLeft={layout === 'grid'}
				activeRight={layout === 'list'}
				onClickLeft={() => handleLayout('grid')}
				onClickRight={() => handleLayout('list')} />
			<div
				className={cx(
					'min-h-screen relative [clip-path:inset(0_0)]',
					layout === 'list' && 'lg:flex lg:flex-row lg:gutter-gap-1 lg:margin-mx-1 lg:py-336',
				)}
				style={{
					height: layout === 'grid' && device === 'desktop' && !slideshowDisabled
						? `${30 * items.length}vh`
						: undefined,
				}}
				ref={containerRef}>

				{/* Intro */}
				<ComingSoonLogo
					logoRef={logoRef} className={cx(
						layout === 'list' && 'lg:fixed lg:top-0 lg:left-0 lg:right-0',
					)} />
				<ComingSoonText surtitle={intro.surtitle} text={intro.text} containerRef={textRef} />

				{/* list  */}
				{layout === 'list' && (
					<Container
						className={cx(
							'flex flex-col relative lg-max:pt-76 lg:order-2 lg-max:min-h-screen',
						)}>
						{!!items?.length && items.map((item, index) => (
							<ProductSliderListItem
								{...item}
								image={item.media.selectedTab === 'video' ? item.media.video?.poster : item.media.image}
								ref={e => {
									if (e) childrenListItemRefs.current[index] = e
								}}
								notifyClick={() => handleNotifyClick(item.item.title)}
								key={`list-item-${index}`}
								index={index}
								locationLabel={locale('Location')} />
						))}
					</Container>
				)}

				<div
					ref={gridSlideshowRef}
					className={cx(
						'flex flex-col bg-white-off z-1',
						layout === 'list'
							? 'sticky lg-max:bottom-0 lg-max:left-0 lg-max:right-0 lg:top-336 lg:h-fit'
							: 'sticky top-0 h-screen',
						'transition-[opacity,visibility] duration-500 ease-quart-out',
						'opacity-0 invisible',
					)}>
					{/* slideshow */}
					<SlideshowNative
						disableLg
						ref={slideshowRef}
						key={slideshowDisabled ? 'disabled' : 'enabled'}
						aria-hidden={layout === 'list'}
						containerClassName={cx(
							'flex-1 flex items-end overflow-clip w-full',
							layout === 'list' && 'hidden',
						)}
						className='flex !items-end w-max relative gutter-gap-1 lg:gap-10 margin-px-1 scroll-px-[var(--margin)]'>
						{!!items?.length && items.map((item, index) => (
							<ProductSliderCard
								{...item}
								getRef={e => {
									if (e) childrenCardRefs.current[index] = e
								}}
								notifyClick={() => handleNotifyClick(item.item.title)}
								key={`card-item-${index}`}
								index={index}
								locationLabel={locale('Location')} />
						))}
					</SlideshowNative>

					{/* navigation */}
					<SlideshowNative
						disableLg={titles.length <= 1}
						ref={headingContainerRef}
						containerClassName={cx(
							layout === 'list' && 'lg:span-w-5',
						)}
						className={cx(
							'flex flex-row items-center justify-between py-34 lg:py-40 bg-white-off',
							'gap-30 margin-px-1 scroll-pl-[var(--margin)]',
							layout === 'list' && 'lg:flex-col lg:items-start lg:gap-24 lg:py-0 lg:pl-0',
						)}>
						{!!titles?.length && titles.map((item, index) => {
							const HeadingTag = index === 0 ? 'h1' : 'h2'
							return (
								<Button
									key={index}
									href='/'
									className='relative text-left'>
									<HeadingTag
										ref={e => { if (e) headingRefs.current[index] = e }}
										className={cx(
											'text-32 w-fit lg-max:max-w-[90vw] font-bold tracking-tighter uppercase leading-none',
											'transition-color duration-300 ease-quart-out',
											index === activeHeading ? 'text-black' : 'text-black/20',
											layout === 'grid' ? 'lg:text-70 xl:text-80' : 'xl:text-50 lg:leading-none',
										)}>
										{item.title}
									</HeadingTag>
									{item.inactive && item.inactive.isDisabled && (
										<Pastil variant='grey' className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 lg:w-120'>{item.inactive.tag}</Pastil>
									)}
								</Button>
							)
						})}
					</SlideshowNative>
					<LayoutSwitch
						className='lg:hidden'
						activeLeft={layout === 'grid'}
						activeRight={layout === 'list'}
						onClickLeft={() => handleLayout('grid')}
						onClickRight={() => handleLayout('list')} />
				</div>
			</div>
			<ProductSliderSlideout
				retreat={activeRetreat}
				isOpen={isOpen}
				onClickClose={onClickClose}
				notifyPanel={notifyPanel} />
		</>
	)
}
